import { useQuery } from '@tanstack/react-query';

import axios from 'axios';
import { getCookie, setCookie } from '../utils/cookies';

export type ChangelogTag = 'new-feature' | 'feature-update' | 'step-update' | 'deprecation' | 'api-update';

type Topic = {
  id: number;
  title: string;
  fancy_title: string;
  slug: string;
  created_at: string;
  isNew?: boolean;
  tags?: ChangelogTag[];
};

export type ChangelogRespone = {
  timestamp: string;
  topic_list: {
    topics: Topic[];
  };
};

const cookieName = 'changelogLastVisit';
const setChangelogLastVisitCookie = (date: Date) => {
  let domain = '.bitrise.io';
  if (window.location.host.match('localhost')) domain = 'localhost';
  if (window.location.host.match('bitrise.dev')) domain = '.bitrise.dev';
  const expires = new Date();
  expires.setMonth(expires.getMonth() + 12);
  setCookie(cookieName, date, {
    cookieOptions: {
      domain,
      expires,
    },
  });
};

const useChangelog = (): {
  topics: Topic[] | undefined;
  hasUnreadTopics: boolean;
  isLoading: boolean;
  isError: boolean;
  setChangelogLastVisitCookie: (date: Date) => void;
} => {
  const changelogLastVisit = getCookie(cookieName);
  const now = new Date();
  const changelogLastVisitDate = changelogLastVisit ? new Date(changelogLastVisit) : now;

  let hasUnreadTopics = false;

  const {
    data: topics,
    isLoading,
    isError,
  } = useQuery<Topic[]>({
    queryKey: ['changelog'],
    queryFn: async () => {
      const { data } = await axios.get<ChangelogRespone>(`https://bitrise.io/changelog_latest.json`);

      return data.topic_list.topics.map((topic) => {
        return { isNew: changelogLastVisitDate.getTime() < new Date(topic.created_at).getTime(), ...topic };
      });
    },
    enabled: true,
  });

  if (topics?.length) {
    hasUnreadTopics = topics.filter((topic) => topic.isNew).length > 0;
  }

  return {
    topics,
    hasUnreadTopics,
    isLoading,
    isError,
    setChangelogLastVisitCookie,
  };
};

export default useChangelog;
